/**
 * Live fallback for the resize controls' drag baselines.
 *
 * Every element's resize hook measures its DOM once, right after mount, and
 * hands the numbers to ResizeAxis / ResizeRatio as props. Inside a Tabs combo
 * an inactive pane is `display: none`, so an element mounted there records 0
 * for every dimension, and nothing re-measures when the pane is shown. The
 * first drag then divides the mouse delta by `0 || 1` and snaps the width to
 * the min/max clamp (the reported "jumps straight to 10%").
 *
 * At mousedown the control is on screen by definition — the user just pressed
 * one of its handles — so it can measure itself: the overlay root is laid out
 * at exactly `width%` of its containing block, and that containing block is
 * the element box the percentage is relative to.
 */
export type ResizeControlsMeasurements = {
  /** Current rendered width of the resizable box, in px. */
  containerWidth: number;
  /** Width of the box that `width%` is relative to, in px. */
  elementDomWidth: number;
};

/**
 * Width only. The overlay is `height: 100%` of its containing block, which is
 * the box's height only when the box is the sole in-flow child — and some
 * callers pass a setting (divider border width) as `containerHeight`, not a
 * measurement — so no height fallback is derived here.
 */
export const measureResizeControls = (
  handle: HTMLElement,
): ResizeControlsMeasurements => {
  const root = handle.parentElement;
  const containingBlock = root?.offsetParent;
  return {
    containerWidth: root?.clientWidth ?? 0,
    elementDomWidth:
      containingBlock instanceof HTMLElement ? containingBlock.clientWidth : 0,
  };
};
